import { CompositeLayer, type CompositeLayerProps } from "@deck.gl/core";
import { PolygonLayer, type PolygonLayerProps } from "@deck.gl/layers";
import along from "@turf/along";
import bearing from "@turf/bearing";
import destination from "@turf/destination";
import distance from "@turf/distance";
import { lineString, point } from "@turf/helpers";

// Arrow dimensions
const DEFAULT_ARROW_LENGTH = 2;
const DEFAULT_ARROW_BASESIZE_MULTIPLIER = 0.5;

/**
 * Create an arrowhead at the end of a path segment.
 * Good explanation of some of the logic can be found in this link:
 *    https://observablehq.com/@reyemtm/finding-perpendicular-lines-with-turfjs
 *
 * @param from segment origin point
 * @param to segment destination point
 * @param arrowLength length of the arrow
 * @param arrowBaseSize size of the arrow base
 * @returns
 */
const createArrowhead = (
  from: [number, number] | [number, number, number],
  to: [number, number] | [number, number, number],
  arrowLength = DEFAULT_ARROW_LENGTH,
  arrowBaseSize = DEFAULT_ARROW_LENGTH * DEFAULT_ARROW_BASESIZE_MULTIPLIER,
) => {
  const BEARING_OFFSET = 90;

  // Calculate horizontal and vertical distances - turf does not compute z coordinate for distances :(
  const horizontalDistance = distance(point(from), point(to), {
    units: "meters",
  });
  const verticalDistance = Math.abs((from.at(2) ?? 0) - (to.at(2) ?? 0));
  const segmentLength = Math.sqrt(
    horizontalDistance ** 2 + verticalDistance ** 2,
  );

  // Clamp arrowLength so it does not exceed the 3D segment length.
  const effectiveArrowLength = Math.min(arrowLength, segmentLength);

  // Project the 3D effective arrow length onto the 2D horizontal plane.
  const effectiveArrowLength2D =
    horizontalDistance > 0
      ? effectiveArrowLength * (horizontalDistance / segmentLength)
      : 0;

  // Create a 2D line from tip ("to") to "from"
  const line = lineString([to, from]);
  // Calculate the base point along the line using the 2D effective length.
  const arrowBasePoint = along(line, effectiveArrowLength2D, {
    units: "meters",
  }).geometry.coordinates;

  // Calculate the bearing (direction) from 'from' to 'to' in 2D
  const lineBearing = bearing(point(from), point(to));

  // Calculate left and right wing endpoints in 2D
  const leftWing = destination(
    point(arrowBasePoint),
    arrowBaseSize,
    lineBearing - BEARING_OFFSET,
    { units: "meters" },
  ).geometry.coordinates;

  const rightWing = destination(
    point(arrowBasePoint),
    arrowBaseSize,
    lineBearing + BEARING_OFFSET,
    { units: "meters" },
  ).geometry.coordinates;

  // Interpolate the z coordinate using the fraction of the 3D segment.
  if (segmentLength > 0) {
    const fraction3D = effectiveArrowLength / segmentLength;
    const arrowBaseZ =
      (to[2] ?? 0) + fraction3D * ((from[2] ?? 0) - (to[2] ?? 0));
    arrowBasePoint[2] = arrowBaseZ;
    leftWing[2] = arrowBaseZ;
    rightWing[2] = arrowBaseZ;
  }

  return [[to, leftWing, rightWing]];
};

export type ArrowHeadLayerProps<T> = {
  data: T[];
  getSourcePosition: (d: T) => [number, number] | [number, number, number];
  getTargetPosition: (d: T) => [number, number] | [number, number, number];
  getArrowDirection?: (d: T) => "source" | "target";
  getFillColor: (
    d: T,
  ) => [number, number, number] | [number, number, number, number] | Uint8Array;
  arrowLength?: number;
  arrowBaseSizeMultiplier?: number;
} & CompositeLayerProps;

/**
 * Deckgl Layer representing the head of an arrow. Given two coordinates,
 * creates an arrow head in either one of the coordinates.
 */
export class ArrowHeadLayer<T> extends CompositeLayer<ArrowHeadLayerProps<T>> {
  renderLayers() {
    const {
      id,
      data,
      getSourcePosition,
      getTargetPosition,
      getFillColor,
      getArrowDirection,
    } = this.props;

    return [
      new PolygonLayer(
        this.getSubLayerProps({
          data,
          extruded: false,
          getFillColor: getFillColor ?? [0, 0, 0],
          getLineWidth: 0,
          getPolygon: (d) => {
            const source = getSourcePosition(d);
            const target = getTargetPosition(d);
            const direction = getArrowDirection(d) ?? "source";

            return createArrowhead(
              direction == "source" ? source : target,
              direction == "source" ? target : source,
              DEFAULT_ARROW_LENGTH,
              DEFAULT_ARROW_LENGTH * DEFAULT_ARROW_BASESIZE_MULTIPLIER,
            );
          },
          id: `arrow-layer-${id}`,
        } as PolygonLayerProps<T>),
      ),
    ];
  }
}
